import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { auth0Audience, Auth0Metadata } from '../../api/API';
import { Student } from '../../api/models/Student';
import { Environment } from '../../Environment';
import { RootState } from '../../model/store';
import { studentSlice } from '../../model/studentSlice';
import { useToken } from '../../util/GetTokenHook';
import { Auth0Context, useAuth0 } from '../Auth0Provider';
import { ButtonState } from '../MNOHSButton';

export const pronouns = ['she / her', 'he / him', 'they / their', 'custom'];

type FormState = {
  photoUrl: string;
  preferredPronoun: string;
  customPronoun: string;
};

export type ProfileScreenPropsV2 = {
  // Outputs
  isEditing: boolean;
  /** Object from global state representing the current user. */
  student: Student | undefined;
  /** Error message to be displayed, if one should be present. */
  error: string;
  /** Whether or not the button is showing an activity indicator. */
  buttonState: ButtonState;
  /** If fetching student */
  isFetchingStudent: boolean;
  // Inputs
  userUpdatedIsEditing: (is: boolean) => void;
  /** Call when the user presses the "Save Profile" button. */
  userPressedSaveProfile: (formState: FormState) => void;
};

export const profileContainerV2 = (ProfileScreen: React.ComponentType<ProfileScreenPropsV2>) => () => {
  const student = useSelector((state: RootState) => state.student.profile);

  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');
  const [buttonState, setButtonState] = useState<ButtonState>('enabled');
  const [isFetchingStudent, setIsFetchingStudent] = useState(false);

  const { api } = Environment.current();
  const { isAuthenticated, getTokenSilently, user } = useAuth0() as Auth0Context;
  const { token, isGettingToken } = useToken();

  const dispatch = useDispatch();
  const personId = user?.[Auth0Metadata.PersonId];

  // To prevent updates on unmount
  const isMounted = useRef(true);
  React.useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Get student if it doesn't exist in the store yet
  React.useEffect(() => {
    if (token && personId && !student?.id) {
      (async () => {
        try {
          setIsFetchingStudent(true);
          const result = await api.getStudentById(personId, token);
          if (isMounted.current) {
            setIsFetchingStudent(false);
            dispatch(studentSlice.actions.receivedStudent({ student: result }));
          }
        } catch (e) {
          toast.error(`${e.message} (Profile)`);
          setIsFetchingStudent(false);
        }
      })();
    }
  }, [token, personId, student?.id]);

  return (
    <ProfileScreen
      isEditing={isEditing}
      student={student}
      error={error}
      buttonState={buttonState}
      isFetchingStudent={isFetchingStudent || isGettingToken}
      userUpdatedIsEditing={setIsEditing}
      userPressedSaveProfile={async formState => {
        try {
          if (!isAuthenticated) throw new Error('You need to be authenticated!');
          if (student?.id === undefined) throw new Error('Student not loaded!');

          const { preferredPronoun, customPronoun, photoUrl } = formState;

          // 1.) Show activity indicator.
          setButtonState('showingActivityIndicator');
          // 2.) Update student
          const token = await getTokenSilently({ audience: auth0Audience });
          const updatedPronoun = preferredPronoun === 'custom' ? customPronoun : preferredPronoun;
          const updatedStudent = await api.updateStudent(
            student.id,
            { pronoun: updatedPronoun, imageBase64: photoUrl },
            token || ''
          );

          // 3.) Dispatch to store
          dispatch(studentSlice.actions.receivedStudent({ student: updatedStudent }));
          // 4.) Always reset the activity indicator
          setButtonState('enabled');
          // 5.) Return to viewing after successful edit
          setIsEditing(false);
        } catch (e) {
          toast.error(e.message);
          setButtonState('enabled');
        }
      }}
    />
  );
};
